import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, ArrowLeft, Download, KeyRound } from "lucide-react";
import { API_URL } from "../config";
import { apiRequest, eligibilityProblem, profileLabel } from "../utils/api";
import {
  CI_TEMPLATES,
  SIGN_SCRIPT,
  DEFAULT_ARTIFACT_PATH,
  SAFE_PATH_RE,
  buildTemplate,
} from "../utils/ciTemplates";

const PROVIDERS = Object.keys(CI_TEMPLATES);
const EXPIRY_CHOICES = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days (recommended)" },
  { days: 180, label: "180 days" },
  { days: 365, label: "1 year (maximum)" },
];

const looksLocal = (url) => /^(https?:\/\/)?(localhost|127\.|0\.0\.0\.0|\[::1\])/i.test(url || "");

const card = { background: "white", border: "1px solid #E2E8F0", borderRadius: 16, padding: 20, marginBottom: 20 };
const h2 = { fontSize: 15, fontWeight: 700, color: "#1F2937", margin: "0 0 4px 0" };
const sub = { fontSize: 12.5, color: "#6B7280", margin: "0 0 14px 0" };

const fmtDate = (value) => (value ? new Date(value).toLocaleString() : "—");

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      style={{
        display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 6,
        background: copied ? "#22C55E" : "#2563EB", border: "none", color: "white",
        fontSize: 12, fontWeight: 500, cursor: "pointer",
      }}
    >
      <Copy size={12} />
      {copied ? "Copied!" : label}
    </button>
  );
}

function CodeBlock({ label, code, fileName }) {
  const handleDownload = () => {
    const blob = new Blob([code], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = (fileName || label).split("/").pop();
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };
  return (
    <div style={{ background: "#1E1E2E", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #2D2D3F" }}>
        <span style={{ fontSize: 13, color: "#94A3B8", fontWeight: 500 }}>{label}</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={handleDownload}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 6, background: "#334155", border: "none", color: "white", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
          >
            <Download size={12} /> Download
          </button>
          <CopyButton text={code} />
        </div>
      </div>
      <pre style={{ margin: 0, padding: 16, fontSize: 12, lineHeight: 1.7, color: "#E2E8F0", overflowX: "auto", maxHeight: 420, fontFamily: "'Fira Code', 'Courier New', monospace", whiteSpace: "pre" }}>
        {code}
      </pre>
    </div>
  );
}

// Shown once, right after a credential is created. The token cannot be retrieved later.
export function TokenReveal({ token, name, onDismiss }) {
  return (
    <div style={{ background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <p style={{ fontSize: 14, fontWeight: 700, color: "#92400E", margin: "0 0 4px 0" }}>
        Copy the token for “{name}” now
      </p>
      <p style={{ fontSize: 12.5, color: "#92400E", margin: "0 0 10px 0" }}>
        It is shown only once and cannot be retrieved later. Store it as a secret named
        <code> IPQSIGN_TOKEN </code> in your CI system. Never commit it to a repository.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <code style={{ flex: 1, background: "white", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 10px", fontSize: 12, wordBreak: "break-all" }}>
          {token}
        </code>
        <CopyButton text={token} label="Copy token" />
      </div>
      <button
        type="button"
        onClick={onDismiss}
        style={{ marginTop: 10, fontSize: 12.5, color: "#92400E", background: "none", border: "none", textDecoration: "underline", cursor: "pointer", padding: 0 }}
      >
        I have stored the token — hide it
      </button>
    </div>
  );
}

export function CredentialTable({ clients, onRevoke, revokingId }) {
  if (!clients || clients.length === 0) {
    return <p className="text-sm text-gray-400">No CI credentials yet.</p>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "#6B7280", borderBottom: "1px solid #E5E7EB" }}>
            <th style={{ padding: "6px 8px" }}>Name</th>
            <th style={{ padding: "6px 8px" }}>Token</th>
            <th style={{ padding: "6px 8px" }}>Digital IDs</th>
            <th style={{ padding: "6px 8px" }}>Expires</th>
            <th style={{ padding: "6px 8px" }}>Last used</th>
            <th style={{ padding: "6px 8px" }}>Status</th>
            <th style={{ padding: "6px 8px" }} />
          </tr>
        </thead>
        <tbody>
          {clients.map((c) => {
            const revoked = String(c.status).toUpperCase() === "REVOKED";
            const expired = !revoked && c.expires_at && new Date(c.expires_at) <= new Date();
            let state = "Active";
            if (revoked) {
              state = "Revoked";
            } else if (expired) {
              state = "Expired";
            }
            const color = state === "Active" ? "#15803D" : "#B91C1C";
            return (
              <tr key={c.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                <td style={{ padding: "8px", fontWeight: 600, color: "#1F2937" }}>{c.name}</td>
                <td style={{ padding: "8px", fontFamily: "monospace" }}>{c.token_prefix}…</td>
                <td style={{ padding: "8px" }}>{(c.allowed_digital_ids || []).map((i) => `#${i}`).join(", ")}</td>
                <td style={{ padding: "8px" }}>{fmtDate(c.expires_at)}</td>
                <td style={{ padding: "8px" }}>{fmtDate(c.last_used_at)}</td>
                <td style={{ padding: "8px", color, fontWeight: 600 }}>{state}</td>
                <td style={{ padding: "8px", textAlign: "right" }}>
                  {!revoked && (
                    <button
                      type="button"
                      onClick={() => onRevoke(c)}
                      disabled={revokingId === c.id}
                      style={{ fontSize: 12, color: "#B91C1C", background: "none", border: "1px solid #FCA5A5", borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}
                    >
                      {revokingId === c.id ? "Revoking…" : "Revoke"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function CreateCredentialForm({ eligibleIds, onCreate, busy, error }) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [days, setDays] = useState(90);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const ready = name.trim().length > 0 && selected.length > 0 && !busy;

  if (!eligibleIds || eligibleIds.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        You have no active Digital IDs that can sign. Create or renew a Digital ID first; a CI
        credential can only be limited to Digital IDs that are usable today.
      </p>
    );
  }

  return (
    <div>
      <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
        Credential name
      </label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. github-release-pipeline"
        maxLength={100}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500 mb-3"
      />

      <p style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", margin: "0 0 4px 0" }}>
        Digital IDs this pipeline may sign with
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
        {eligibleIds.map((d) => (
          <label key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} />
            <span>
              {d.identity_name} <small>(#{d.id})</small> — {d.algorithm}, {profileLabel(d.identity_type)}
            </span>
          </label>
        ))}
      </div>

      <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
        Expires after
      </label>
      <select
        value={days}
        onChange={(e) => setDays(Number(e.target.value))}
        className="px-3 py-2 border border-gray-200 rounded-lg text-[13px] mb-3"
      >
        {EXPIRY_CHOICES.map((c) => (
          <option key={c.days} value={c.days}>{c.label}</option>
        ))}
      </select>

      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

      <button
        type="button"
        disabled={!ready}
        onClick={() => onCreate({ name: name.trim(), allowed_digital_ids: selected, expires_in_days: days }, () => { setName(""); setSelected([]); })}
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 8, border: "none",
          background: ready ? "#2563EB" : "#93C5FD", color: "white", fontSize: 13, fontWeight: 600,
          cursor: ready ? "pointer" : "not-allowed",
        }}
      >
        <KeyRound size={14} /> {busy ? "Creating…" : "Create credential"}
      </button>
    </div>
  );
}

export default function CICDSetup() {
  const navigate = useNavigate();

  const [digitalIds, setDigitalIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [newToken, setNewToken] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [actionError, setActionError] = useState("");

  const [provider, setProvider] = useState("github");
  const [artifactPath, setArtifactPath] = useState(DEFAULT_ARTIFACT_PATH);
  const [templateIdChoice, setTemplateIdChoice] = useState(null);

  const eligibleIds = digitalIds.filter((d) => !eligibilityProblem(d));

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [ids, creds] = await Promise.all([
        apiRequest("/api/digital-id/list"),
        apiRequest("/api/ci-clients"),
      ]);
      setDigitalIds(ids.identities || []);
      setClients(creds.clients || []);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (body, resetForm) => {
    setCreating(true);
    setCreateError("");
    try {
      const data = await apiRequest("/api/ci-clients", { method: "POST", json: body });
      setNewToken({ token: data.token, name: data.client.name });
      setClients((prev) => [data.client, ...prev]);
      setTemplateIdChoice(data.client.allowed_digital_ids[0]);
      resetForm();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (client) => {
    if (!window.confirm(`Revoke “${client.name}”? Pipelines using it stop working immediately.`)) return;
    setRevokingId(client.id);
    setActionError("");
    try {
      await apiRequest(`/api/ci-clients/${client.id}/revoke`, { method: "PUT" });
      setClients((prev) => prev.map((c) => (c.id === client.id ? { ...c, status: "REVOKED" } : c)));
    } catch (err) {
      setActionError(err.message);
    } finally {
      setRevokingId(null);
    }
  };

  const digitalIdForTemplate = templateIdChoice ?? eligibleIds[0]?.id ?? null;
  const pathProblem = artifactPath && !SAFE_PATH_RE.test(artifactPath)
    ? "Use only letters, digits and . _ / @ + - in the path. The template keeps its default until this is fixed."
    : "";
  const template = buildTemplate(provider, {
    apiUrl: API_URL,
    digitalId: digitalIdForTemplate,
    artifactPath,
  });

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", maxWidth: 900 }}>
      <button
        onClick={() => navigate("/choose-method")}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft size={14} /> Back to method selection
      </button>

      <p style={{ fontSize: 24, fontWeight: 700, color: "#1F2937" }} className="mb-1">CI/CD Pipeline Setup</p>
      <p className="text-sm text-gray-400 mb-5">
        Sign artifacts automatically on every build. The pipeline signs, verifies the result on the
        server, and publishes only if verification passes.
      </p>

      {loading && <p className="text-sm text-gray-400">Loading…</p>}
      {loadError && <p className="text-sm text-red-600 mb-4">{loadError}</p>}

      {!loading && !loadError && (
        <>
          {/* 1. CREDENTIALS */}
          <div style={card}>
            <p style={h2}>1. Create a CI credential</p>
            <p style={sub}>
              A credential belongs to you, works only for the Digital IDs you choose, only sees the
              artifacts it uploaded itself, always expires, and can be revoked at any time.
            </p>

            {newToken && (
              <TokenReveal token={newToken.token} name={newToken.name} onDismiss={() => setNewToken(null)} />
            )}

            <CreateCredentialForm eligibleIds={eligibleIds} onCreate={handleCreate} busy={creating} error={createError} />
          </div>

          <div style={card}>
            <p style={h2}>Your CI credentials</p>
            {actionError && <p className="text-sm text-red-600 mb-2">{actionError}</p>}
            <CredentialTable clients={clients} onRevoke={handleRevoke} revokingId={revokingId} />
          </div>

          {/* 2. PIPELINE */}
          <div style={card}>
            <p style={h2}>2. Add the signing script to your repository</p>
            <p style={sub}>
              Save it in your repository (for example <code>scripts/sign-artifact.sh</code>) and make it
              executable. Needs bash, curl, jq and sha256sum (preinstalled on GitHub-hosted and Azure-hosted
              Linux runners). If any step fails, or verification does not pass, it exits non-zero and writes
              nothing. If your pipeline file cannot be edited, put the settings in a committed
              <code> sign-artifact.conf</code> next to the script and keep the token in a file on the CI server
              (<code>IPQSIGN_TOKEN_FILE</code>).
            </p>
            <CodeBlock label="ci/sign-artifact.sh" code={SIGN_SCRIPT} fileName="sign-artifact.sh" />
          </div>

          <div style={card}>
            <p style={h2}>3. Choose your CI platform</p>

            <div className="flex gap-2 mb-3" style={{ flexWrap: "wrap" }}>
              {PROVIDERS.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setProvider(key)}
                  style={{
                    padding: "6px 14px", fontSize: 13, fontWeight: 500, borderRadius: 8, cursor: "pointer",
                    border: provider === key ? "1.5px solid #2563EB" : "1.5px solid #E2E8F0",
                    background: provider === key ? "#EFF6FF" : "white",
                    color: provider === key ? "#2563EB" : "#6B7280",
                  }}
                >
                  {CI_TEMPLATES[key].label}
                </button>
              ))}
            </div>

            <ol style={{ fontSize: 13, color: "#374151", paddingLeft: 20, margin: "0 0 14px 0", lineHeight: 1.7 }}>
              {CI_TEMPLATES[provider].setup.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>

            <table style={{ fontSize: 12.5, borderCollapse: "collapse", marginBottom: 14 }}>
              <tbody>
                <tr>
                  <td style={{ padding: "3px 14px 3px 0", fontFamily: "monospace" }}>IPQSIGN_API_URL</td>
                  <td style={{ padding: "3px 0", fontFamily: "monospace" }}>{API_URL}</td>
                </tr>
                <tr>
                  <td style={{ padding: "3px 14px 3px 0", fontFamily: "monospace" }}>IPQSIGN_DIGITAL_ID</td>
                  <td style={{ padding: "3px 0" }}>
                    {eligibleIds.length > 0 ? (
                      <select
                        value={digitalIdForTemplate ?? ""}
                        onChange={(e) => setTemplateIdChoice(Number(e.target.value))}
                        className="px-2 py-1 border border-gray-200 rounded-lg text-[12.5px]"
                      >
                        {eligibleIds.map((d) => (
                          <option key={d.id} value={d.id}>{d.id} — {d.identity_name} ({profileLabel(d.identity_type)})</option>
                        ))}
                      </select>
                    ) : "—"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "3px 14px 3px 0", fontFamily: "monospace" }}>IPQSIGN_TOKEN</td>
                  <td style={{ padding: "3px 0", color: "#6B7280" }}>the credential from step 1 — keep it in the secret store, never in a file</td>
                </tr>
              </tbody>
            </table>

            {looksLocal(API_URL) && (
              <p className="text-sm" style={{ color: "#B45309", marginBottom: 12 }}>
                This app currently talks to <code>{API_URL}</code>, which your CI runners cannot reach.
                Use the public https:// address of the I-PQSign server as <code>IPQSIGN_API_URL</code>.
                The script refuses plain http:// URLs.
              </p>
            )}

            <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              Path of the file your build produces
            </label>
            <input
              value={artifactPath}
              onChange={(e) => setArtifactPath(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500 mb-1"
              style={{ fontFamily: "monospace", maxWidth: 420 }}
            />
            {pathProblem && <p className="text-xs" style={{ color: "#B45309", marginBottom: 8 }}>{pathProblem}</p>}

            <div style={{ marginTop: 12 }}>
              <CodeBlock label={CI_TEMPLATES[provider].fileName} code={template} fileName={CI_TEMPLATES[provider].fileName} />
            </div>

            <p className="text-xs text-gray-500">
              Exit codes: 0 signed and verified · 1 configuration problem · 2 server or network failure ·
              3 verification or integrity check failed. Results are written to <code>signed-output/</code>:
              the signed artifact, signature, manifest, public key and <code>signing-result.json</code>.
            </p>
          </div>
        </>
      )}
    </div>
  );
}